import type {
  GroundSample,
  InputFrame,
  RunDescriptor,
  SimulationState,
  SimulationWorld,
  Vec3,
} from '../contracts/game'
import { FIXED_DT } from './FixedStepRunner'
import type { GameplayTuning } from './GameplayTuning'

const MAX_STEERING_Q = 32_767
const HASH_PRECISION = 10_000

const flatWorld: SimulationWorld = {
  sampleGround(_x, _z, out) {
    out.height = 0
    out.normal.x = 0
    out.normal.y = 1
    out.normal.z = 0
    return true
  },
  sweepPlayer() {
    return null
  },
}

function createVector(x = 0, y = 0, z = 0): Vec3 {
  return { x, y, z }
}

function createInitialState(tuning: GameplayTuning, descriptor: RunDescriptor): SimulationState {
  const position = createVector(0, tuning.playerRadius, 0)
  return {
    tick: 0,
    descriptor: { ...descriptor },
    player: {
      position,
      previousPosition: { ...position },
      lateralPosition: 0,
      lateralVelocity: 0,
      forwardVelocity: tuning.initialSpeed,
      verticalVelocity: 0,
      grounded: true,
      groundNormal: createVector(0, 1, 0),
      distance: 0,
      alive: true,
      deathCause: 'none',
    },
  }
}

export class Simulation {
  private readonly tuning: GameplayTuning
  private readonly world: SimulationWorld
  private readonly groundSample: GroundSample = { height: 0, normal: createVector(0, 1, 0) }
  private readonly trackCenter = createVector()
  private mutableState: SimulationState
  private invincible = false

  public constructor(tuning: GameplayTuning, world: SimulationWorld = flatWorld) {
    this.tuning = tuning
    this.world = world
    this.mutableState = createInitialState(tuning, {
      seed: 0,
      tuningVersion: tuning.version,
      replayVersion: 1,
    })
  }

  public get state(): Readonly<SimulationState> {
    return this.mutableState
  }

  public start(descriptor: RunDescriptor): void {
    if (descriptor.tuningVersion !== this.tuning.version) {
      throw new Error(`Unsupported tuning version: ${descriptor.tuningVersion}`)
    }
    this.mutableState = createInitialState(this.tuning, descriptor)
    this.world.reset?.(descriptor.seed)
  }

  public reset(): void {
    this.mutableState = createInitialState(this.tuning, this.mutableState.descriptor)
  }

  public kill(cause: 'obstacle' | 'fall' | 'void' = 'obstacle'): void {
    if (this.invincible) return
    this.mutableState.player.alive = false
    this.mutableState.player.deathCause = cause
  }

  public setInvincible(enabled: boolean): void {
    this.invincible = enabled
  }

  public step(input: InputFrame): void {
    const state = this.mutableState
    const player = state.player
    if (!player.alive) return
    if (input.tick !== state.tick) throw new Error(`Expected input tick ${state.tick}, received ${input.tick}`)

    player.previousPosition.x = player.position.x
    player.previousPosition.y = player.position.y
    player.previousPosition.z = player.position.z
    this.world.update?.(state.tick, player.position.z)

    const steering = Math.max(-MAX_STEERING_Q, Math.min(MAX_STEERING_Q, input.steeringQ)) / MAX_STEERING_Q
    const airControl = player.grounded ? 1 : this.tuning.steeringAirControl
    player.lateralVelocity += steering * this.tuning.lateralAcceleration * airControl * FIXED_DT
    player.lateralVelocity *= Math.exp(-this.tuning.lateralDrag * FIXED_DT)
    player.lateralVelocity = Math.max(
      -this.tuning.maximumLateralSpeed,
      Math.min(this.tuning.maximumLateralSpeed, player.lateralVelocity),
    )

    player.forwardVelocity = this.tuning.initialSpeed
      + (this.tuning.maximumSpeed - this.tuning.initialSpeed)
        * (1 - Math.exp(-player.distance * this.tuning.speedRamp))

    player.lateralPosition += player.lateralVelocity * FIXED_DT
    player.distance += player.forwardVelocity * FIXED_DT
    this.trackCenter.x = 0
    this.trackCenter.y = 0
    this.trackCenter.z = player.distance
    this.world.sampleTrackCenter?.(player.distance, this.trackCenter)
    player.position.x = this.trackCenter.x + player.lateralPosition
    player.position.z = this.trackCenter.z

    const hasGround = this.world.sampleGround(player.position.x, player.position.z, this.groundSample)
    if (hasGround) {
      const targetHeight = this.groundSample.height + this.tuning.playerRadius
      const landingGap = player.position.y - targetHeight
      if (player.grounded || (
        player.verticalVelocity <= 0
        && landingGap <= this.tuning.landingSnapDistance
        && landingGap >= -this.tuning.playerRadius * 0.5
      )) {
        player.position.y = targetHeight
        player.verticalVelocity = -this.groundSample.normal.z
          / Math.max(0.1, this.groundSample.normal.y)
          * player.forwardVelocity
        player.grounded = true
        player.groundNormal.x = this.groundSample.normal.x
        player.groundNormal.y = this.groundSample.normal.y
        player.groundNormal.z = this.groundSample.normal.z
      } else {
        player.grounded = false
      }
    } else {
      player.grounded = false
    }

    if (!player.grounded) {
      player.verticalVelocity -= this.tuning.gravity * FIXED_DT
      player.position.y += player.verticalVelocity * FIXED_DT
    }

    const collisionCause = this.world.sweepPlayer(
      player.previousPosition,
      player.position,
      this.tuning.playerRadius,
    )
    if (collisionCause !== null && !this.invincible) {
      player.alive = false
      player.deathCause = collisionCause
    } else if (player.position.y < this.tuning.killPlane) {
      if (this.invincible) {
        player.position.x = 0
        player.position.y = this.tuning.playerRadius
        player.verticalVelocity = 0
        player.grounded = true
      } else {
        player.alive = false
        player.deathCause = 'fall'
      }
    }

    state.tick += 1
  }

  public createStateHash(): string {
    const { player, tick, descriptor } = this.mutableState
    const values = [
      tick,
      descriptor.seed,
      player.position.x,
      player.position.y,
      player.position.z,
      player.lateralVelocity,
      player.lateralPosition,
      player.forwardVelocity,
      player.verticalVelocity,
      player.distance,
      player.grounded ? 1 : 0,
      player.alive ? 1 : 0,
    ]
    return values.map((value) => Math.round(value * HASH_PRECISION).toString(36)).join(':')
  }
}
